import { signInResultSchema } from '@wodi/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Button, Notice, TextField } from '@wodi/ui';
import { useState } from 'react';

import { api, ApiError } from '../api/client.ts';
import { safeNext, sessionQuery } from '../api/session.ts';
import styles from './sign-in.module.css';
import { useSignOut } from './use-sign-out.ts';

const route = getRouteApi('/sign-in/code');

export function SignInCodePage() {
  const { next } = route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useSignOut();
  const [code, setCode] = useState('');
  const [missing, setMissing] = useState(false);

  const verify = useMutation({
    mutationFn: (value: string) =>
      api('POST', '/auth/two-factor/verify', { body: { code: value }, schema: signInResultSchema }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      await navigate({ to: safeNext(next) });
    },
    onError: () => {
      setCode('');
    },
  });

  const error =
    verify.error instanceof ApiError
      ? verify.error.message
      : verify.error
        ? 'Something went wrong. Try again.'
        : null;

  return (
    <main className={styles.page}>
      <div className={styles.column}>
        <div className={styles.brand}>Wodi</div>
        <h1 className={styles.title}>Enter your code</h1>
        <p className={styles.lead}>
          Open your authenticator app and enter the six-digit code shown for Wodi.
        </p>
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            const value = code.trim();
            if (value.length < 6) {
              setMissing(true);
              return;
            }
            setMissing(false);
            verify.mutate(value);
          }}
        >
          <TextField
            label="Code"
            description="Lost your phone? Enter one of your recovery codes instead."
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={setCode}
            {...(missing ? { errorMessage: 'Enter the six-digit code, or a recovery code.' } : {})}
          />
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isPending={verify.isPending}
              pendingLabel="Checking…"
            >
              Continue
            </Button>
            <Button variant="quiet" onPress={signOut}>
              Sign out
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
